import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export default function EntityDialog({
    open,
    onOpenChange,
    title,
    description,
    fields,
    initial,
    onSubmit,
    testId,
}) {
    const [form, setForm] = useState(() => defaults(fields, initial));
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    // Reset form each time dialog opens
    React.useEffect(() => {
        if (open) {
            setForm(defaults(fields, initial));
            setError("");
        }
    }, [open, initial, fields]);

    const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        const payload = coerce(fields, form);
        const res = await onSubmit(payload);
        setSaving(false);
        if (res.ok) onOpenChange(false);
        else setError(res.error);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid={testId}>
                <DialogHeader>
                    <DialogTitle className="font-display">{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {fields.map((f) => (
                            <div
                                key={f.key}
                                className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}
                            >
                                <Label className="text-xs">
                                    {f.label}
                                    {f.required && <span className="ml-0.5 text-red-500">*</span>}
                                </Label>
                                <FieldInput field={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />
                            </div>
                        ))}
                    </div>
                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            {error}
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} data-testid={`${testId}-submit`}>
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function defaults(fields, initial) {
    const out = {};
    for (const f of fields) {
        const v = initial?.[f.key];
        if (v !== undefined && v !== null) out[f.key] = f.type === "tags" && Array.isArray(v) ? v.join(", ") : v;
        else if (f.type === "boolean") out[f.key] = f.default ?? true;
        else if (f.type === "number") out[f.key] = f.default ?? 0;
        else if (f.type === "select") out[f.key] = f.default ?? f.options?.[0]?.value ?? "";
        else out[f.key] = f.default ?? "";
    }
    return out;
}

function coerce(fields, form) {
    const out = {};
    for (const f of fields) {
        let v = form[f.key];
        if (v === "" || v === undefined) {
            if (f.type === "number") v = 0;
            else if (f.type === "boolean") v = f.default ?? true;
            else if (f.type === "tags") v = [];
            else v = null;
        } else if (f.type === "number") v = Number(v);
        else if (f.type === "tags") v = String(v).split(",").map((t) => t.trim()).filter(Boolean);
        out[f.key] = v;
    }
    return out;
}

function FieldInput({ field, value, onChange }) {
    const testId = `field-${field.key}`;
    if (field.type === "textarea") {
        return (
            <Textarea
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={3}
                className="text-sm"
            />
        );
    }
    if (field.type === "select") {
        return (
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testId} className="h-9">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {field.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }
    if (field.type === "boolean") {
        return <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />;
    }
    return (
        <Input
            data-testid={testId}
            type={field.type === "number" ? "number" : "text"}
            step={field.type === "number" ? "any" : undefined}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="h-9"
        />
    );
}
